import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Subject } from "rxjs";
import { UploadItem } from "./upload-item.model";
import { UploadFailure, UploadRequest, UploadSuccess } from "./upload-request";

export interface CeloFileUploaderConfig {
  /** Maximum number of items allowed in the queue. Extra files are rejected by addFiles(). */
  queueLimit?: number;
  /** If set, files whose MIME type is not listed are rejected by addFiles(). */
  allowedMimeTypes?: string[];
}

export type UploadRejectionReason = "mimeType" | "queueLimit";

export interface UploadRejection {
  file: File;
  reason: UploadRejectionReason;
}

/**
 * HttpClient-backed replacement for ng2-file-upload's FileUploader.
 * Owns a queue of UploadItems and reports per-item progress/success/error.
 */
export class CeloFileUploader {
  readonly queue: UploadItem[] = [];

  private readonly addedSource = new Subject<UploadItem[]>();
  private readonly progressSource = new Subject<UploadItem>();
  private readonly successSource = new Subject<UploadSuccess>();
  private readonly errorSource = new Subject<UploadFailure>();
  private readonly completeSource = new Subject<void>();
  private readonly rejectedSource = new Subject<UploadRejection>();

  /** Emits the items just added to the queue. */
  readonly added$ = this.addedSource.asObservable();
  /** Emits an item whenever its upload progress changes. */
  readonly progress$ = this.progressSource.asObservable();
  readonly success$ = this.successSource.asObservable();
  readonly error$ = this.errorSource.asObservable();
  /** Emits once when no item is left uploading. */
  readonly complete$ = this.completeSource.asObservable();
  /** Emits when a file is rejected by addFiles() (bad MIME type or queue full). */
  readonly rejected$ = this.rejectedSource.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly config: CeloFileUploaderConfig = {}
  ) {}

  get isUploading(): boolean {
    return this.queue.some((item) => item.isUploading);
  }

  addFiles(files: ArrayLike<File> | null | undefined): UploadItem[] {
    if (!files) {
      return [];
    }

    const added: UploadItem[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (
        this.config.allowedMimeTypes &&
        !this.config.allowedMimeTypes.includes(file.type)
      ) {
        this.rejectedSource.next({ file, reason: "mimeType" });
        continue;
      }
      if (this.isQueueFull()) {
        this.rejectedSource.next({ file, reason: "queueLimit" });
        continue;
      }
      const item = new UploadItem(file);
      this.queue.push(item);
      added.push(item);
    }

    if (added.length) {
      this.addedSource.next(added);
    }
    return added;
  }

  remove(item: UploadItem): void {
    const index = this.queue.indexOf(item);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  }

  clear(): void {
    this.queue.length = 0;
  }

  /** Upload the given items (defaults to the whole queue). */
  upload(request: UploadRequest, items: UploadItem[] = [...this.queue]): void {
    for (const item of items) {
      this.uploadItem(request, item);
    }
  }

  private uploadItem(request: UploadRequest, item: UploadItem): void {
    item.status = "uploading";
    item.progress = 0;
    item.error = undefined;

    const url =
      typeof request.url === "function" ? request.url(item) : request.url;
    const httpRequest = new HttpRequest(
      request.method ?? "POST",
      url,
      request.buildBody(item),
      {
        reportProgress: true,
        headers: new HttpHeaders(request.headers ?? {}),
      }
    );

    this.http.request(httpRequest).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          item.progress = Math.round((100 * event.loaded) / event.total);
          this.progressSource.next(item);
        } else if (event instanceof HttpResponse) {
          item.status = "uploaded";
          item.progress = 100;
          item.response = event.body;
          this.successSource.next({ item, response: event.body });
          this.emitCompleteIfDone();
        }
      },
      error: (error) => {
        item.status = "error";
        item.error =
          error?.message ?? "Upload failed. Please try again.";
        this.errorSource.next({
          item,
          status: error?.status ?? 0,
          response: error?.error,
        });
        this.emitCompleteIfDone();
      },
    });
  }

  private isQueueFull(): boolean {
    return (
      this.config.queueLimit !== undefined &&
      this.queue.length >= this.config.queueLimit
    );
  }

  private emitCompleteIfDone(): void {
    if (!this.isUploading) {
      this.completeSource.next();
    }
  }
}
